import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartAccount, Employee, LocaleCode } from '@/api/models';
import type { EmployeeStatusFilter } from '@/api/handlers/resources/salaries/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useChartOfAccounts,
    useEmployeesQuery,
    usePayrollRunsQuery,
} from '@/api/queries';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import {
    resolveLabel,
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import {
    canReadSalaries,
    deriveSalariesAccess,
    type SalariesAccess,
} from './salariesAccess';
import {
    computeTieOut,
    registerEmptyState,
    toEmployeeRows,
    toRunListRows,
    toSummaryFigures,
    type EmployeeRow,
    type RateText,
    type RegisterEmptyState,
    type RunListRow,
    type SalariesTieOut,
    type SavedEmployee,
    type SummaryFigures,
} from './salariesMeta';
import type { EmployeeSaved } from './components/employeeFormDrawer';
import { useCurrentSalaryRates } from './useSalaryRates';

/** Long enough to collapse a burst of typing, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 250;

/** Which drawer / dialog is open. Only one at a time. */
type OverlayState =
    | { kind: 'NONE' }
    | { kind: 'FORM'; employee: Employee | null; instanceId: number }
    | { kind: 'RUN' }
    /** A run is addressed by its journal entry id — it has no other identity. */
    | { kind: 'RUN_DETAIL'; journalEntryId: string };

export type UseSalariesPageResult = {
    locale: LocaleCode;
    /**
     * What this session may do here. Anything but `full` means the screen
     * renders an explanation and issues no requests — every salaries endpoint is
     * OrgAdmin-only, reads included.
     */
    access: SalariesAccess;
    /** The rates in force today; null when no set governs them. */
    rates: RateText | null;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    status: EmployeeStatusFilter;
    search: string;
    onStatusChange: (status: EmployeeStatusFilter) => void;
    onSearchChange: (search: string) => void;
    /** Null until the first load resolves. */
    figures: SummaryFigures | null;
    /** Null while the chart of accounts is still loading. */
    tieOut: SalariesTieOut | null;
    rows: EmployeeRow[];
    emptyState: RegisterEmptyState;
    accounts: ChartAccount[];
    runRows: RunListRow[];
    /**
     * The runs feed is still in flight. It is **not** folded into `isLoading`:
     * the register does not need to wait on it. But the run list must not answer
     * "no payroll runs yet" while the answer is unknown, so it gets its own flag.
     */
    isRunsLoading: boolean;
    overlay: OverlayState;
    /**
     * The confirmation for the last write the form drawer made, or `null`.
     *
     * The register used to confirm nothing: the drawer closed and the row was
     * either in the table or it was not. That reads as "did that work?" for the
     * one second before the list settles, and it is the same event the Control
     * Center has to confirm — so it is a fact the page holds now, not a shrug.
     */
    saved: SavedEmployee | null;
    onSaved: EmployeeSaved;
    onDismissSaved: () => void;
    onOpenCreate: () => void;
    onOpenEdit: (employeeId: string) => void;
    onOpenRun: () => void;
    onOpenRunDetail: (journalEntryId: string) => void;
    onCloseOverlay: () => void;
};

/**
 * Drives the Salaries register.
 *
 * It reads three independent feeds and joins two of them only for the
 * accrued-vs-ledger comparison: the register (what posted runs have accrued),
 * the payroll runs, and the **existing** chart-of-accounts / trial-balance query
 * (the general ledger). That separation is deliberate — the register is not
 * allowed to certify itself, so the comparison is made against the same balances
 * every other accounting screen reads.
 *
 * Before any of that it decides **whether it may read at all**. Every endpoint
 * behind this screen is OrgAdmin-with-a-password; for anyone else all three
 * queries can only 403, so they are disabled and the screen explains itself.
 * Discovering that from a 403 would tell the user nothing they can act on.
 *
 * All derivation is pure and lives in `salariesMeta`; this hook owns fetching,
 * filter state and which overlay is open.
 */
export const useSalariesPage = (): UseSalariesPageResult => {
    const { i18n, t } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // Read once per mount, like `useSettingsPage`: the session cannot change
    // without a navigation that remounts this page.
    const access = React.useMemo(
        () => deriveSalariesAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadSalaries(access);

    const [status, setStatus] = React.useState<EmployeeStatusFilter>('ACTIVE');
    const [search, setSearch] = React.useState('');
    const [overlay, setOverlay] = React.useState<OverlayState>({
        kind: 'NONE',
    });
    const [saved, setSaved] = React.useState<SavedEmployee | null>(null);
    /** Bumped on each form open so a reopened drawer never inherits a draft. */
    const formInstanceRef = React.useRef(0);

    // The register is filtered server-side (the real endpoint takes `search`), so
    // the query key holds a debounced copy: typing a name should cost one
    // request, not ten. The input stays fully controlled by `search`.
    const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

    const employeesQuery = useEmployeesQuery(
        { status, search: debouncedSearch },
        canRead,
    );
    const runsQuery = usePayrollRunsQuery(canRead);
    // The chart is readable at BranchAdmin, but there is nothing to compare it
    // against here, so it is not fetched either.
    const chart = useChartOfAccounts({ locale, enabled: canRead });
    const rates = useCurrentSalaryRates(canRead);

    const { data: employeesData, error: employeesError } = unwrapStatement(
        employeesQuery.data,
        employeesQuery.error,
    );
    const { data: runsData } = unwrapStatement(runsQuery.data, runsQuery.error);

    const employees = React.useMemo(
        () => employeesData?.items ?? [],
        [employeesData],
    );
    const accounts = chart.data ?? [];

    const figures = React.useMemo(
        () => (employeesData ? toSummaryFigures(employeesData.summary) : null),
        [employeesData],
    );

    const tieOut = React.useMemo(
        () =>
            employeesData
                ? computeTieOut(employeesData.summary, chart.data)
                : null,
        [employeesData, chart.data],
    );

    const rows = React.useMemo(
        () => toEmployeeRows(employees, locale),
        [employees, locale],
    );

    const runRows = React.useMemo(
        () => toRunListRows(runsData?.items ?? [], t),
        [runsData, t],
    );

    const emptyState = React.useMemo<RegisterEmptyState>(() => {
        if (!employeesData) {
            return 'NONE';
        }
        return registerEmptyState({
            visibleCount: employeesData.items.length,
            activeCount: employeesData.summary.activeEmployeeCount,
            hasSearch: debouncedSearch.trim() !== '',
            // `totalEmployeeCount` ignores the filter on purpose: a Terminated
            // filter over a register with no leavers returns zero rows, and
            // answering that with "No employees yet" would be false.
            totalCount: employeesData.summary.totalEmployeeCount,
        });
    }, [employeesData, debouncedSearch]);

    const onSaved = React.useCallback<EmployeeSaved>(
        (employee, meta) => {
            setSaved({
                name: resolveLabel(employee.name, locale, employee.id),
                isEdit: meta.isEdit,
                alreadyExisted: meta.alreadyExisted,
            });
        },
        [locale],
    );

    const onDismissSaved = React.useCallback(() => setSaved(null), []);

    const onOpenCreate = React.useCallback(() => {
        // A new write supersedes the last confirmation: two banners stacked up
        // would leave the reader deciding which one is about what they just did.
        setSaved(null);
        formInstanceRef.current += 1;
        setOverlay({
            kind: 'FORM',
            employee: null,
            instanceId: formInstanceRef.current,
        });
    }, []);

    const onOpenEdit = React.useCallback(
        (employeeId: string) => {
            const employee =
                employees.find((candidate) => candidate.id === employeeId) ??
                null;
            if (!employee) {
                return;
            }
            formInstanceRef.current += 1;
            setOverlay({
                kind: 'FORM',
                employee,
                instanceId: formInstanceRef.current,
            });
        },
        [employees],
    );

    const onOpenRun = React.useCallback(() => {
        setOverlay({ kind: 'RUN' });
    }, []);

    const onOpenRunDetail = React.useCallback((journalEntryId: string) => {
        setOverlay({ kind: 'RUN_DETAIL', journalEntryId });
    }, []);

    const onCloseOverlay = React.useCallback(() => {
        setOverlay({ kind: 'NONE' });
    }, []);

    const onRetry = React.useCallback(() => {
        employeesQuery.refetch();
        runsQuery.refetch();
        chart.refetch();
    }, [employeesQuery, runsQuery, chart]);

    return {
        locale,
        access,
        rates,
        // A disabled query never leaves `isLoading`, so a refused session would
        // otherwise skeleton forever. It is not loading; it is not going to.
        isLoading: canRead && employeesQuery.isLoading,
        error: employeesError,
        onRetry,
        status,
        search,
        onStatusChange: setStatus,
        onSearchChange: setSearch,
        figures,
        tieOut,
        rows,
        emptyState,
        accounts,
        runRows,
        isRunsLoading: canRead && runsQuery.isLoading,
        overlay,
        saved,
        onSaved,
        onDismissSaved,
        onOpenCreate,
        onOpenEdit,
        onOpenRun,
        onOpenRunDetail,
        onCloseOverlay,
    };
};
